/* eslint-disable @typescript-eslint/no-base-to-string */
import {
    FailureResult,
    SuccessResult,
    failureResult,
    successResult,
    unwrapOrThrowResult,
} from '@deltasierra/type-utilities';
import moment from 'moment-timezone';
import { MappedTimePeriodArray } from '../MappedTimePeriodArray';
import { TimePeriod } from '../TimePeriod';
import { TimePeriodUnit } from '../TimePeriodUnit';

export interface TimePeriodArrayFromRangeOptions {
    startOfFirstDate?: boolean;
    endOfLastDate?: boolean;
}

export type EmptyTimePeriodArray = {
    length: false;
};

/**
 * A TimePeriodArray is a list of time periods in a specific unit.
 * It does not have to be consecutive, but each time period is required
 * to be of exactly the same length.
 */
export class TimePeriodArray {
    private constructor(
        public readonly timePeriods: TimePeriod[],
        public readonly timePeriodUnit: TimePeriodUnit,
    ) {}

    public static of(
        timePeriods: TimePeriod[],
        timePeriodUnit: TimePeriodUnit,
    ): FailureResult | SuccessResult<TimePeriodArray> {
        if (timePeriods.length <= 0) {
            return failureResult(
                new Error('There was an error creating the TimePeriodArray, no time periods were created.'),
            );
        }

        if (
            timePeriodUnit === 'milliseconds' ||
            timePeriodUnit === 'seconds' ||
            timePeriodUnit === 'minutes' ||
            timePeriodUnit === 'hours' ||
            timePeriodUnit === 'days'
        ) {
            /**
             * Here we will compare diffs using milliseconds.
             * Do not use the precise function in moment, and do not use anything larger than millis for under 1 day.
             * The milliseconds are used because most of the time, that is the lowest form
             * of accuracy we want. The precise flag will make a compare of dates sometimes
             * fail because some dates in the year might not be exactly the same length? Or
             * some funky date logic, so don't worry about it, just use milliseconds because
             * each time period should have exactly the same millis.
             *
             * Every period unit higher than hours will not be safe to compare this way
             */
            const timePeriodDiffs = timePeriods.map(timePeriod =>
                moment(timePeriod.startDate).diff(timePeriod.endDate, 'milliseconds'),
            );

            if (
                !timePeriodDiffs.reduce<{ tally: boolean; prev?: number }>(
                    (prev, cur) => {
                        if (prev.tally && prev.prev) {
                            if (prev.prev === cur) {
                                return {
                                    prev: cur,
                                    tally: true,
                                };
                            } else {
                                return {
                                    tally: false,
                                };
                            }
                        } else {
                            return {
                                prev: cur,
                                tally: prev.tally,
                            };
                        }
                    },
                    {
                        prev: undefined,
                        tally: true,
                    },
                ).tally
            ) {
                return failureResult(
                    new Error(
                        `There was an error creating the TimePeriodArray, the time periods do not have the same diffs.
                    timePeriods: ${timePeriods}
                    timePeriodUnit: ${timePeriodUnit}
                    timePeriodDiffs: ${timePeriodDiffs}
                `,
                    ),
                );
            }
        }

        return successResult(new TimePeriodArray(timePeriods, timePeriodUnit));
    }

    /**
     * Shorthand for selecting a period of time descriptively
     * e.g: TimePeriodArray.last(30, 'days') -> returns yesterday and the 30 days before it
     *
     * @param amount - The amount
     * @param unit - The unit for the array
     * @param includeCurrent - Whether we should include the current unit of time?
     * @returns - The time period array
     */
    public static last(amount: number, unit: TimePeriodUnit, includeCurrent = false): TimePeriodArray {
        const endDate = includeCurrent ? moment.utc() : moment.utc().subtract(1, unit);
        const startDate = moment.utc(endDate).subtract(amount - 1, unit);
        return unwrapOrThrowResult(
            TimePeriodArray.fromRange(startDate.toDate(), endDate.toDate(), unit, {
                endOfLastDate: true,
                startOfFirstDate: true,
            }),
        );
    }

    public static fromTimePeriod(
        timePeriod: TimePeriod,
        timePeriodUnit: TimePeriodUnit,
        options?: TimePeriodArrayFromRangeOptions,
    ): FailureResult | SuccessResult<TimePeriodArray> {
        return TimePeriodArray.fromRange(timePeriod.startDate, timePeriod.endDate, timePeriodUnit, options);
    }

    /**
     * Creates a TimePeriodArray from a start and end date, in units
     *
     * @param startDate - The start date
     * @param endDate - The end date
     * @param timePeriodUnit - The unit of time for the TimePeriodArray
     * @param options - The optional options
     * @returns - The TimePeriodArray for the range
     */
    // eslint-disable-next-line max-statements
    public static fromRange(
        startDate: Date,
        endDate: Date,
        timePeriodUnit: TimePeriodUnit,
        options?: TimePeriodArrayFromRangeOptions,
    ): FailureResult | SuccessResult<TimePeriodArray> {
        const { endOfLastDate = false, startOfFirstDate = false } = options ?? {};
        // This should be converted to the start of the period
        const startDateMoment = startOfFirstDate
            ? moment.utc(startDate).startOf(timePeriodUnit)
            : moment.utc(startDate);
        // This should be converted to the end of the period
        const endDateMoment = endOfLastDate ? moment.utc(endDate).endOf(timePeriodUnit) : moment.utc(endDate);

        if (!startDateMoment.isBefore(endDateMoment)) {
            return failureResult(
                new Error('Cannot create TimePeriodArray from range: Start date must be before the End date'),
            );
        }

        const timePeriods: TimePeriod[] = [];

        // We now calculate the rounded down time between
        const timeBetween = endDateMoment.diff(startDateMoment, timePeriodUnit, true);

        if (timeBetween <= 0) {
            return failureResult(
                new Error(
                    `Cannot create TimePeriodArray from range: Time between start and end date is not enough to satisfy at least 1 period.
                    ${startDateMoment.toDate()} ${endDateMoment.toDate()}`,
                ),
            );
        }

        for (let i = 0; i <= timeBetween; i++) {
            // Add the unit of time to the start date
            const forDate = moment.utc(startDateMoment).add(i, timePeriodUnit);
            // We will fetch from the start of the day, to the end of the day for all days in the period
            const startDateForPeriod = moment.utc(forDate).startOf(timePeriodUnit);
            const endDateForPeriod = moment.utc(forDate).endOf(timePeriodUnit);

            // We need to check that the dates are not outside our range
            if (!(startDateForPeriod.isBefore(startDateMoment) || endDateForPeriod.isAfter(endDateMoment))) {
                const newTimePeriod = TimePeriod.of(startDateForPeriod.toDate(), endDateForPeriod.toDate());
                if (!newTimePeriod.success) {
                    return failureResult(newTimePeriod.failure);
                }

                timePeriods.push(newTimePeriod.value);
            }
        }

        if (!timePeriods.length) {
            return failureResult(new Error('Cannot create TimePeriodArray from range: The range created no periods'));
        }

        return TimePeriodArray.of(timePeriods, timePeriodUnit);
    }

    public get length(): number {
        return this.timePeriods.length;
    }

    /**
     * Reduces the list of time periods to a single time period
     *
     * @returns - A time period of the first and last dates
     */
    public reduceToTimePeriod(): TimePeriod {
        const timePeriod = TimePeriod.of(
            this.timePeriods.reduce(
                (prev, cur) => (cur.startDate < prev ? cur.startDate : prev),
                this.timePeriods[0].startDate,
            ),
            this.timePeriods.reduce(
                (prev, cur) => (cur.endDate > prev ? cur.endDate : prev),
                this.timePeriods[0].endDate,
            ),
        );

        if (!timePeriod.success) {
            throw timePeriod.failure;
        }
        return timePeriod.value;
    }

    /**
     * Maps the array to something else
     *
     * @param mappingFunc - Map to another value
     * @returns - The new mapped array
     */
    public map<U>(mappingFunc: (value: TimePeriod) => U): MappedTimePeriodArray<U> {
        return new MappedTimePeriodArray(
            this.timePeriods.map(timePeriod => ({ timePeriod, value: mappingFunc(timePeriod) })),
        );
    }

    /**
     * Maps the array to another value and removes entries with missing values
     *
     * @param mappingFunc - Map to another value
     * @returns - The new mapped array
     */
    public mapAndExcludeMissing<U>(mappingFunc: (value: TimePeriod) => U | null | undefined): MappedTimePeriodArray<U> {
        return this.map(mappingFunc).filter(
            (val): val is Exclude<typeof val, null | undefined> => val !== null && val !== undefined,
        );
    }

    /**
     * Joins the array of time periods, with another array that can map to dates.
     * Useful for mapping a time period to a result from a request.
     *
     * @param array - The array you want to join with
     * @param periodMappingFunc - The function to map a value from the array, to a date
     * @param lastUpdatedMappingFunc - The function to map a value from the array, to the newest date (optional)
     * @returns - An array of time periods mapped to a value or undefined if it doesnt exist for the period
     */
    public join<U>(
        array: U[],
        periodMappingFunc: (val: U) => Date,
        lastUpdatedMappingFunc = periodMappingFunc,
    ): MappedTimePeriodArray<U | undefined> {
        const mappedWithDate = array.map(val => ({
            lastUpdatedDate: lastUpdatedMappingFunc(val),
            periodDate: periodMappingFunc(val),
            val,
        }));

        return this.map(
            timePeriod =>
                mappedWithDate
                    .filter(val => timePeriod.isInRange(val.periodDate))
                    .reduce<(typeof mappedWithDate)[number] | undefined>((prev, cur) => {
                        if (prev) {
                            return prev.lastUpdatedDate.getTime() > cur.lastUpdatedDate.getTime() ? prev : cur;
                        } else {
                            return cur;
                        }
                    }, undefined)?.val,
        );
    }

    /**
     * Joins the array of time periods, with another array that can map to dates.
     * Useful for mapping a time period to a result from a request.
     *
     * @param array - The array you want to join with
     * @param periodMappingFunc - The function to map a value from the array, to a date
     * @param lastUpdatedMappingFunc - The function to map a value from the array, to the newest date (optional)
     * @returns - An array of time periods mapped to a value or undefined if it doesnt exist for the period
     */
    public joinAndExcludeMissing<U>(
        array: U[],
        periodMappingFunc: (val: U) => Date,
        lastUpdatedMappingFunc = periodMappingFunc,
    ): MappedTimePeriodArray<Exclude<U, null | undefined>> {
        return this.join(array, periodMappingFunc, lastUpdatedMappingFunc).filter(
            (val): val is Exclude<typeof val, null | undefined> => val !== null && val !== undefined,
        );
    }

    /**
     * Copies the TimePeriodArray if its time periods meet the requirements
     *
     * @param ifFunc - The test function to see if the period is in the new array
     * @returns - The new TimePeriodArray or an empty object
     */
    public filter(ifFunc: (value: TimePeriod) => boolean): EmptyTimePeriodArray | TimePeriodArray {
        const mappedArray = this.timePeriods.filter(ifFunc);

        if (mappedArray.length) {
            const timePeriodArray = TimePeriodArray.of(mappedArray, this.timePeriodUnit);

            if (!timePeriodArray.success) {
                throw timePeriodArray.failure;
            }

            return timePeriodArray.value;
        }

        return { length: false };
    }

    /**
     * Processes a MappedTimePeriodArray<T> so we can handle results and missing values
     *
     * @param mappedTimePeriodArray - The MappedTimePeriodArray<T>
     * @param resultMappingFunc - The result mapping function
     * @param missingMappingFunc - The missing mapping function
     * @returns - A tuple of the [results, missingValues] mapped
     */
    public process<T, N, M>(
        mappedTimePeriodArray: MappedTimePeriodArray<T>,
        resultMappingFunc: (newValue: T, timePeriod: TimePeriod) => N,
        missingMappingFunc: (missingTimePeriod: TimePeriod) => M,
    ): [N[], M[]] {
        const results = this.timePeriods.map(
            (timePeriod): { timePeriod: TimePeriod } & ({ missing: false; value: T } | { missing: true }) => {
                const found = mappedTimePeriodArray.internalMap.find(val => val.timePeriod === timePeriod);

                if (found) {
                    return {
                        missing: false,
                        timePeriod,
                        value: found.value,
                    };
                } else {
                    return {
                        missing: true,
                        timePeriod,
                    };
                }
            },
        );

        return [
            results
                .filter((val): val is typeof val & { missing: false } => !val.missing)
                .map(val => resultMappingFunc(val.value, val.timePeriod)),

            results
                .filter((val): val is typeof val & { missing: true } => val.missing)
                .map(val => missingMappingFunc(val.timePeriod)),
        ];
    }

    /**
     * Processes a MappedTimePeriodArray<T> so we can handle results and missing values.
     * Excludes undefined and null values once the value is mapped.
     *
     * @param mappedTimePeriodArray - The MappedTimePeriodArray<T>
     * @param resultMappingFunc - The result mapping function
     * @param missingMappingFunc - The missing mapping function
     * @returns - A tuple of the [results, missingValues] mapped
     */
    public processAndExcludeMissing<T, N, M>(
        mappedTimePeriodArray: MappedTimePeriodArray<T>,
        resultMappingFunc: (newValue: T, timePeriod: TimePeriod) => N | null | undefined,
        missingMappingFunc: (missingTimePeriod: TimePeriod) => M | null | undefined,
    ): [Array<Exclude<N, null | undefined>>, Array<Exclude<M, null | undefined>>] {
        const [results, missing] = this.process(mappedTimePeriodArray, resultMappingFunc, missingMappingFunc);

        return [
            results.filter((val): val is Exclude<typeof val, null | undefined> => val !== null && val !== undefined),
            missing.filter((val): val is Exclude<typeof val, null | undefined> => val !== null && val !== undefined),
        ];
    }
}
